import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, MapPin, Filter, AlertCircle, Briefcase } from 'lucide-react';
import { useJobStore } from '@/store/jobStore';
import { useAuthStore } from '@/store/authStore';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { AdvancedSearch } from '@/components/search/AdvancedSearch';
import { NetworkError } from '@/components/ui/ErrorStates';
import { ProviderCard } from '@/components/ui/ProviderCard';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SkeletonProviderCard } from '@/components/ui/SkeletonLoaders';
import { EnhancedEmptyState } from '@/components/ui/EnhancedEmptyState';
import { FadeIn } from '@/components/ui/AnimatedComponents';

export function SearchPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { searchProviders, searchResults, isLoading, searchFilters, setSearchFilters } = useJobStore();
  const { isAuthenticated } = useAuthStore();

  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const state = location.state as { query?: string; location?: string };
    if (state?.query || state?.location) {
      const initialFilters = {
        query: state.query || '',
        location: state.location || '',
        category: '',
        radius: 25,
        priceRange: { min: 0, max: 1000 },
        rating: 0,
        availability: 'any' as const,
        providerType: 'any' as const,
        verified: false,
        premium: false,
        emergency: false,
        sortBy: 'relevance' as const
      };
      handleSearch(initialFilters);
    } else if (searchFilters && (searchFilters.query || searchFilters.location)) {
      setHasSearched(true);
    }
  }, [location.state]);

  const handleSearch = async (filters: any) => {
    setError(null);
    setHasSearched(true);

    try {
      await searchProviders(filters.query, filters.location, filters);
      setSearchFilters(filters);
    } catch (err) {
      setError('Failed to search providers. Please try again.');
    }
  };

  const handleRetry = () => {
    if (searchFilters) {
      handleSearch(searchFilters);
    }
  };

  const handleContactProvider = (providerId: string) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/chat/new?providerId=${providerId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Skip Links */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Find Professionals' }]} />

        <div className="page-header mt-4">
          <h1 className="page-title">Find Local Professionals</h1>
          <p className="page-description">Search for verified service providers in your area.</p>
        </div>

        {/* Search Interface */}
        <div className="mb-6 sm:mb-8" id="main-content">
          <AdvancedSearch
            onSearch={handleSearch}
            initialFilters={searchFilters}
            type="providers"
          />
        </div>

        {/* Results */}
        <div className="min-h-[400px]" role="region" aria-label="Search results">
          {error ? (
            <NetworkError onRetry={handleRetry} />
          ) : isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {[...Array(6)].map((_, i) => (
                <SkeletonProviderCard key={i} />
              ))}
            </div>
          ) : !hasSearched ? (
            <EnhancedEmptyState
              icon={Search}
              title="Start your search"
              description="Enter a service or location above to find qualified professionals."
              action={{
                label: 'Browse Categories',
                onClick: () => navigate('/'),
                icon: <Search className="w-4 h-4" />
              }}
              size="md"
              illustration="gradient"
            />
          ) : searchResults.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                  {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} found
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {searchResults.map((provider, index) => (
                  <FadeIn key={provider.id} delay={index * 50}>
                    <ProviderCard
                      provider={provider}
                      onRequestQuote={() => navigate(`/providers/${provider.id}/quote`)}
                      onViewProfile={() => navigate(`/providers/${provider.id}`)}
                    />
                  </FadeIn>
                ))}
              </div>
            </>
          ) : (
            <EnhancedEmptyState
              icon={Search}
              title="No providers found"
              description="We couldn't find any providers matching your search criteria. Try adjusting your filters or expanding your search area."
              action={{
                label: 'Clear Filters',
                onClick: () => handleSearch({ query: '', location: '' }),
                icon: <Filter className="w-4 h-4" />
              }}
              size="md"
              illustration="pattern"
            />
          )}
        </div>
      </div>
    </div>
  );
}
